import React, { Component } from 'react';
import moment from 'moment';
import http from '../../../../utils/http';
import { toast } from 'react-toastify';
import { Box, Button, Chip, CircularProgress, Divider, Grid, IconButton, Radio, RadioGroup, Stack, Tabs, Typography } from '@mui/material';
import classes from '../docFwb.module.css';
import styles from '../../../../custom/style.module.css';
import { CustomButton, CustomRadio, CustomTab } from '../../../../custom/components';
import { FlexDiv, StyledCard, StyledCardTitle } from '../../../../custom/componentsnew';
import InputSelectField from '../../../../custom/InputSelectField';
import InputDateField from '../../../../custom/InputDateField';
import InputTextField from '../../../../custom/InputTextField';
import InputRadioGroup from '../../../../custom/InputRadioGroup';
import { AddIcon, countryCode } from '../../../../custom/constants';
import AccountingInfoModal from './AccountingInfoModal';
import FwbDiffViewer from './FwbDiffViewer';
import FwbDiffModal from './FwbDiffModal';
import FwbSaveModal from './FwbSaveModal';
import FwbVersionPopover from './FwbVersionPopover';
import FwbVersionPreviewModal from './FwbVersionPreviewModal';
import FwbHistoryPopover from './FwbHistoryPopover';
import { prepareFormDataForSubmission } from './fwbParser';
import { ArrowUpward, KeyboardArrowDown, InfoOutlined } from '@mui/icons-material';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers';
import theme from 'custom/theme';

const GRID_SPACING = 2;

// Input UI components for the form
const Section = ({
  title,
  children,
  className,
  sx = {},
}) => (
  <StyledCard
    sx={{
      border: '1px solid var(--primaryblue)',
      ...sx,
    }}
    className={className || ''}
  >
    {(typeof title === 'string' ? title.trim() !== '' : Boolean(title)) && (
      <StyledCardTitle mb={1.5} sx={{ flexShrink: 0 }}>
        {title}
      </StyledCardTitle>
    )}
    {children}
  </StyledCard>
);

const FormGroup = ({
  label,
  type = 'text',
  select = false,
  date = false,
  value = '',
  onChange,
  options = [],
  variant = 'standard',
  error = false,
  disabled = false,
  ariaLabel,
  id,
  sx,
  placeholder,
  className,
}) => {
  const displayVal = value !== undefined && value !== null ? value : '';
  return (
    <>
      {select ? (
        <InputSelectField
          label={label}
          value={displayVal}
          onChange={onChange || (() => {})}
          options={options}
          variant={variant}
          error={error}
          disabled={disabled}
          fullWidth
          id={id}
          inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
          sx={sx}
        />
      ) : date ? (
        <InputDateField
          label={label}
          value={displayVal}
          onChange={onChange || (() => {})}
          variant={variant}
          error={error}
          disabled={disabled}
          fullWidth
          placeholder='DD MMM YYYY'
          format='DD MMM YYYY'
          inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
        />
      ) : (
        <InputTextField
          label={label}
          value={displayVal}
          onChange={onChange || (() => {})}
          type={type}
          variant={variant}
          error={error}
          disabled={disabled}
          fullWidth
          className={className}
          sx={sx}
          id={id}
          placeholder={placeholder}
          inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
        />
      )}
    </>
  );
};

export const getDefaultChatMessages = (data, docId) => [
  {
    id: `init-${docId || data?.docId || data?.id || '1'}`,
    sender: 'ai',
    text: `Hello! I am CABA AI. How can I assist you with this Air Waybill (${data?.awbNumber || 'FWB'})?`,
    time: moment().format('hh:mm A'),
  },
];

export const resolveChatMessages = (cabaAiChat, data, fallback) => {
  const parseChatArray = (chat) => {
    if (Array.isArray(chat)) return chat;
    if (typeof chat === 'string') {
      try {
        const parsed = JSON.parse(chat);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // Not a valid JSON array
      }
    }
    return null;
  };

  const parsedPropChat = parseChatArray(cabaAiChat);
  if (parsedPropChat && parsedPropChat.length > 0) return parsedPropChat;

  const parsedDataChat = parseChatArray(data?.caba_ai_chat);
  if (parsedDataChat && parsedDataChat.length > 0) return parsedDataChat;

  return fallback !== undefined ? fallback : getDefaultChatMessages(data, data?.docId);
};

class FwbData extends Component {
  constructor(props) {
    super(props);
    const initialDefaultChat = getDefaultChatMessages(props.data, props.docId || props.data?.docId);
    this.state = {
      data: props.data || null,
      modal: false,
      rightSectionView: 'alerts', // 'alerts' | 'chatbot'
      chatMessages: resolveChatMessages(props.cabaAiChat, props.data, initialDefaultChat),
      chatInputText: '',
      isAiResponding: false,
      editableFwbText: props.fwbRawText || '',
      isManualEditing: false,
      previewingVersion: null,
      isStackedPreview: false,
      versionPopoverAnchorEl: null,
      historyPopoverAnchorEl: null,
      previewVersionModalOpen: false,
      previewVersionData: null,
      saveModalOpen: false,
      saveModalAnchorEl: null,
      saveContext: null,
      isSavingFwb: false,
      isUpdatingFwb: false,
      hasFormChanges: false,
      modifiedPaths: {},
      initialData: props.data || null,
      formDiffModalOpen: false,
      formDiffOldText: '',
      formDiffNewText: '',
      formDiffGuidelines: null,
      alertDecisions: {},
      alertInputs: {},
    };
    this.chatBottomRef = React.createRef();
    this.chatMessagesContainerRef = React.createRef();
    this.gutterRef = React.createRef();
    this.previewResizeObserver = null;
    this.previewGroupElement = null;
    this.previewResizeFrame = null;
    this.pendingStackedPreview = false;
  }

  componentWillUnmount() {
    if (this.previewResizeObserver) {
      this.previewResizeObserver.disconnect();
    }
    if (this.previewResizeFrame !== null) {
      cancelAnimationFrame(this.previewResizeFrame);
      this.previewResizeFrame = null;
    }
  }

  handlePreviewGroupElement = (element) => {
    if (this.previewGroupElement === element) return;

    if (this.previewResizeObserver) {
      this.previewResizeObserver.disconnect();
      this.previewResizeObserver = null;
    }
    if (this.previewResizeFrame !== null) {
      cancelAnimationFrame(this.previewResizeFrame);
      this.previewResizeFrame = null;
    }
    this.previewGroupElement = element;

    if (!element || typeof ResizeObserver === 'undefined') return;

    this.previewResizeObserver = new ResizeObserver(([entry]) => {
      this.pendingStackedPreview = entry.contentRect.width < 760;
      if (this.previewResizeFrame !== null) return;

      this.previewResizeFrame = requestAnimationFrame(() => {
        this.previewResizeFrame = null;
        this.setState((prevState) => (
          prevState.isStackedPreview === this.pendingStackedPreview
            ? null
            : { isStackedPreview: this.pendingStackedPreview }
        ));
      });
    });
    this.previewResizeObserver.observe(element);
  };

  componentDidMount() {
    if (this.props.data || this.props.cabaAiChat) {
      const activeDocId = this.props.docId || this.props.data?.docId;
      const initialDefaultChat = getDefaultChatMessages(this.props.data, activeDocId);
      this.setState({
        data: this.props.data,
        chatMessages: resolveChatMessages(this.props.cabaAiChat, this.props.data, initialDefaultChat),
      });
    }
    if (this.props.fwbRawText) {
      this.setState({ editableFwbText: this.props.fwbRawText });
    }
  }

  componentDidUpdate(prevProps) {
    const prevDocId = prevProps.docId || prevProps.data?.docId;
    const currentDocId = this.props.docId || this.props.data?.docId;
    const docChanged = Boolean(
      (currentDocId && (!prevDocId || String(currentDocId) !== String(prevDocId))) ||
      (!currentDocId && prevDocId)
    );

    if (docChanged) {
      const freshDefaultChat = getDefaultChatMessages(this.props.data, currentDocId);
      this.setState({
        data: this.props.data,
        initialData: this.props.data,
        modifiedPaths: {},
        hasFormChanges: false,
        editableFwbText: this.props.fwbRawText || '',
        isManualEditing: false,
        chatMessages: resolveChatMessages(this.props.cabaAiChat, this.props.data, freshDefaultChat),
        chatInputText: '',
        isAiResponding: false,
        alertDecisions: {},
        alertInputs: {},
        previewingVersion: null,
      }, () => {
        if (this.state.rightSectionView === 'chatbot') {
          setTimeout(this.scrollToBottom, 50);
        }
      });
    } else if ((!this.state.data && this.props.data) || (!prevProps.data && this.props.data) || (prevProps.data !== this.props.data && !this.state.hasFormChanges)) {
      const isOnlyInitialGreeting =
        Array.isArray(this.state.chatMessages) &&
        this.state.chatMessages.length === 1 &&
        String(this.state.chatMessages[0]?.id || '').startsWith('init-');
      const fallback = isOnlyInitialGreeting
        ? getDefaultChatMessages(this.props.data, currentDocId)
        : this.state.chatMessages;

      this.setState({
        data: this.props.data,
        initialData: this.props.data,
        modifiedPaths: {},
        hasFormChanges: false,
        chatMessages: resolveChatMessages(this.props.cabaAiChat, this.props.data, fallback),
      });
    } else if (this.props.cabaAiChat !== prevProps.cabaAiChat) {
      const prevArray = Array.isArray(prevProps.cabaAiChat) ? prevProps.cabaAiChat : [];
      const currentArray = Array.isArray(this.props.cabaAiChat) ? this.props.cabaAiChat : [];
      const bothEmpty = prevArray.length === 0 && currentArray.length === 0;

      if (!bothEmpty) {
        const isOnlyInitialGreeting =
          Array.isArray(this.state.chatMessages) &&
          this.state.chatMessages.length === 1 &&
          String(this.state.chatMessages[0]?.id || '').startsWith('init-');
        const fallback = isOnlyInitialGreeting
          ? getDefaultChatMessages(this.props.data, currentDocId)
          : this.state.chatMessages;

        const resolved = resolveChatMessages(this.props.cabaAiChat, this.props.data, fallback);
        this.setState({ chatMessages: resolved });
      }
    }
    if (this.props.fwbRawText && this.props.fwbRawText !== prevProps.fwbRawText && !this.state.isManualEditing) {
      this.setState({
        editableFwbText: this.props.fwbRawText,
        previewingVersion: null,
        alertDecisions: {},
        alertInputs: {},
      });
    }
  }

  handleTextareaScroll = (e) => {
    if (this.gutterRef && this.gutterRef.current) {
      this.gutterRef.current.scrollTop = e.target.scrollTop;
    }
  };

  handleFwbTextChange = (e) => {
    const text = e.target.value;
    this.setState({
      editableFwbText: text,
      isManualEditing: true,
    });
  };

  handleCancelManualEdit = () => {
    const baseText = this.state.previewingVersion?.fwb || this.props.fwbRawText || '';
    this.setState({
      editableFwbText: baseText,
      isManualEditing: false,
    });
  };

  handleOpenManualSave = (e) => {
    this.setState({
      saveModalOpen: true,
      saveModalAnchorEl: e.currentTarget,
      saveContext: {
        source: 'manual',
        fwbToSave: this.state.editableFwbText,
      },
    });
  };

  handleOpenAiSave = (msg, anchorEl) => {
    this.setState({
      saveModalOpen: true,
      saveModalAnchorEl: anchorEl,
      saveContext: {
        source: 'ai',
        fwbToSave: msg.refinedFwb,
        messageId: msg.id,
        prompt: msg.prompt,
        guidelines: msg.guidelines,
        modelId: msg.modelId,
      },
    });
  };

  handleRejectAiDiff = (msgId) => {
    const docId = this.state.data?.docId || this.props.data?.docId || this.props.docId;
    if (docId) {
      http.post('/doc_fwb/reject-diff', {
        docId,
        messageId: msgId,
      }).catch(() => {});
    }
    if (this.props.onRejectDiff) {
      this.props.onRejectDiff(msgId, docId);
    }
    this.setState((prevState) => ({
      chatMessages: prevState.chatMessages.map((msg) =>
        String(msg.id) === String(msgId)
          ? {
            ...msg,
            diffStatus: 'rejected',
          }
          : msg
      ),
    }));
  };

  handleSaveModalConfirm = ({ saveType, versionName, targetVersion }) => {
    const { saveContext } = this.state;
    if (!saveContext || !saveContext.fwbToSave) return;

    this.setState({ isSavingFwb: true });
    if (this.props.onSaveFwb) {
      this.props
        .onSaveFwb({
          fwbMessage: saveContext.fwbToSave,
          saveType,
          versionName,
          targetVersion,
          source: saveContext.source,
          prompt: saveContext.prompt,
          guidelines: saveContext.guidelines,
          modelId: saveContext.modelId,
          messageId: saveContext.messageId,
        })
        .then(() => {
          this.setState((prevState) => {
            let updatedMessages = prevState.chatMessages;
            if (saveContext.messageId) {
              updatedMessages = prevState.chatMessages.map((msg) =>
                String(msg.id) === String(saveContext.messageId)
                  ? {
                    ...msg,
                    diffStatus: 'accepted',
                  }
                  : msg
              );
            }
            return {
              saveModalOpen: false,
              saveModalAnchorEl: null,
              saveContext: null,
              isSavingFwb: false,
              isManualEditing: false,
              formDiffModalOpen: false,
              formDiffOldText: '',
              formDiffNewText: '',
              formDiffGuidelines: null,
              hasFormChanges: false,
              modifiedPaths: {},
              initialData: saveContext.formData || prevState.data,
              editableFwbText: saveContext.fwbToSave,
              chatMessages: updatedMessages,
              previewingVersion: null,
            };
          }, () => {
            if (this.props.onFwbManualUpdated && saveContext.source === 'manual') {
              this.props.onFwbManualUpdated({
                fwbMessage: saveContext.fwbToSave,
                guidelines: saveContext.guidelines,
                formData: saveContext.formData || this.state.data,
              });
            }
          });
        })
        .catch(() => {
          this.setState({ isSavingFwb: false });
        });
    }
  };

  getIssueKey = (issue, index) => {
    if (!issue) return `issue-${index}`;
    const code = issue.code || 'TACT';
    const line = issue.line !== undefined && issue.line !== null ? issue.line : '0';
    const field = issue.field || '';
    return `${code}_L${line}_${field}_${index}`;
  };

  handleAlertDecisionChange = (issueKey, decision) => {
    this.setState((prevState) => ({
      alertDecisions: {
        ...prevState.alertDecisions,
        [issueKey]: decision,
      },
    }));
  };

  handleAlertInputChange = (issueKey, value) => {
    this.setState((prevState) => ({
      alertInputs: {
        ...prevState.alertInputs,
        [issueKey]: value,
      },
    }));
  };

  handleFixAllWithAi = () => {
    const tactIssues = this.props.tactIssues || this.props.data?.tactIssues || [];
    const acceptedPrompts = [];

    tactIssues.forEach((issue, idx) => {
      const issueKey = this.getIssueKey(issue, idx);
      if (this.state.alertDecisions[issueKey] === 'accept') {
        const basePrompt = (issue?.suggestedPrompt || issue?.message || '').trim();
        const extraText = (this.state.alertInputs[issueKey] || '').trim();

        if (basePrompt && extraText) {
          acceptedPrompts.push(`${basePrompt} (Additional user instructions: ${extraText})`);
        } else if (basePrompt) {
          acceptedPrompts.push(basePrompt);
        } else if (extraText) {
          acceptedPrompts.push(extraText);
        }
      }
    });

    if (acceptedPrompts.length === 0) return;

    const combinedPrompt =
      acceptedPrompts.length > 1
        ? acceptedPrompts.map((p, i) => `${i + 1}. ${p}`).join('\n')
        : acceptedPrompts[0];

    this.setState(
      {
        rightSectionView: 'chatbot',
        chatInputText: combinedPrompt,
      },
      () => {
        setTimeout(this.scrollToBottom, 50);
      }
    );
  };

  handleApplyAlertViaAi = (issueKey, issue) => {
    const customInput = this.state.alertInputs[issueKey];
    const promptToUse = (customInput && customInput.trim()) || issue?.suggestedPrompt || issue?.message;
    if (!promptToUse) return;

    this.setState({ rightSectionView: 'chatbot' }, () => {
      this.handleSendPrompt(promptToUse);
    });
  };

  handleTabChange = (_, view) => {
    this.setState({ rightSectionView: view }, () => {
      if (view === 'chatbot') {
        setTimeout(this.scrollToBottom, 50);
      }
    });
  };

  scrollToBottom = () => {
    if (this.chatMessagesContainerRef && this.chatMessagesContainerRef.current) {
      const container = this.chatMessagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    } else if (this.chatBottomRef && this.chatBottomRef.current) {
      this.chatBottomRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  handleSendChatMessage = () => {
    this.handleSendPrompt(this.state.chatInputText);
  };

  handleSendPrompt = (promptText) => {
    const prompt = (promptText || '').trim();
    if (!prompt || this.state.isAiResponding) return;

    const userMsg = {
      id: Date.now().toString(),
      sender: 'user',
      text: prompt,
      time: moment().format('hh:mm A'),
    };

    this.setState(
      (prevState) => ({
        chatMessages: [...prevState.chatMessages, userMsg],
        chatInputText: '',
        isAiResponding: true,
      }),
      () => {
        this.scrollToBottom();
        this.submitRefinePrompt(prompt);
      }
    );
  };

  submitRefinePrompt = (prompt) => {
    const fwbMessage =
      this.props.fwbRawText ||
      (Array.isArray(this.props.fwbLines) && this.props.fwbLines.length > 0 ? this.props.fwbLines.join('\n') : '');
    const docId = this.state.data?.docId || this.props.data?.docId || this.props.docId;

    if (!fwbMessage) {
      const errorMsg = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: 'No FWB EDI message found to modify. Please generate the FWB first.',
        time: moment().format('hh:mm A'),
      };
      this.setState(
        (prevState) => ({
          chatMessages: [...prevState.chatMessages, errorMsg],
          isAiResponding: false,
        }),
        this.scrollToBottom
      );
      return;
    }

    const targetDocId = docId;

    http
      .post('/doc_fwb/refine', {
        docId: targetDocId,
        fwbMessage,
        prompt,
      })
      .then((res) => {
        const currentDocId = this.state.data?.docId || this.props.data?.docId || this.props.docId;
        const isStillSameDoc = targetDocId && currentDocId && String(targetDocId) === String(currentDocId);

        const payload = res?.data?.data;
        const aiMsg = {
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: 'Here are the suggested FWB modifications based on your prompt:',
          time: moment().format('hh:mm A'),
          isDiff: true,
          oldFwb: fwbMessage,
          refinedFwb: payload?.fwb || '',
          guidelines: payload?.guidelines || null,
          modelId: payload?.modelId || null,
          diffStatus: 'pending',
          prompt: prompt,
        };

        if (isStillSameDoc) {
          const nextMessages = payload?.caba_ai_chat || [
            ...this.state.chatMessages,
            payload?.aiMsg || aiMsg,
          ];

          this.setState(
            {
              chatMessages: nextMessages,
              isAiResponding: false,
            },
            () => {
              this.scrollToBottom();
              if (this.props.onFwbRefined && payload) {
                this.props.onFwbRefined(payload, targetDocId);
              }
            }
          );
        } else {
          // Document switched while async request was in flight - do not mutate current document chat
          if (this.props.onFwbRefined && payload) {
            this.props.onFwbRefined(payload, targetDocId);
          }
        }
      })
      .catch((err) => {
        console.error('Error refining FWB:', err);
        const currentDocId = this.state.data?.docId || this.props.data?.docId || this.props.docId;
        const isStillSameDoc = targetDocId && currentDocId && String(targetDocId) === String(currentDocId);

        if (isStillSameDoc) {
          const errorText = err?.response?.data?.message || 'Failed to apply changes to FWB. Please try again.';
          const aiMsg = {
            id: (Date.now() + 1).toString(),
            sender: 'ai',
            text: errorText,
            time: moment().format('hh:mm A'),
          };
          this.setState(
            (prevState) => ({
              chatMessages: [...prevState.chatMessages, aiMsg],
              isAiResponding: false,
            }),
            this.scrollToBottom
          );
        }
      });
  };

  getAirportOptions = (currentVal) => {
    const defaultHubs = ['MIA', 'JFK', 'LHR', 'FRA', 'ORD', 'LAX', 'DXB', 'SIN', 'HKG', 'IST', 'AMS', 'CDG', 'YYZ', 'NRT', 'ICN', 'PEK', 'PVG', 'BOG', 'GRU', 'EZE', 'CLT', 'ATL', 'DFW'];
    const stations = this.props.stations || [];
    const set = new Set([...(currentVal ? [currentVal] : []), ...stations, ...defaultHubs]);
    return Array.from(set).filter(Boolean);
  };

  getCarrierOptions = (currentVal) => {
    const defaultCarriers = ['LH', 'TK', 'BA', 'AA', 'DL', 'UA', 'EK', 'QR', 'AF', 'KL', 'PO', 'CV', 'FX', '5Y', 'UC', 'QT', 'AM'];
    const airlines = this.props.airlines || [];
    const set = new Set([...(currentVal ? [currentVal] : []), ...airlines, ...defaultCarriers]);
    return Array.from(set).filter(Boolean);
  };

  getCountryOptions = (currentVal) => {
    const list = Array.isArray(countryCode)
      ? countryCode.map((c) => ({
        value: c.code,
        label: `${c.code} - ${c.name}` 
      }))
      : [];
    if (currentVal && !list.some((c) => c.value === currentVal)) {
      list.unshift({
        value: currentVal,
        label: currentVal 
      });
    }
    return list;
  };

  getCurrencyOptions = (currentVal) => {
    const currencies = ['USD', 'EUR', 'GBP', 'TRY', 'CAD', 'JPY', 'INR', 'AED', 'AUD', 'CHF', 'CNY', 'SGD', 'BRL', 'MXN', 'SEK', 'NOK', 'DKK'];
    const set = new Set([...(currentVal ? [currentVal] : []), ...currencies]);
    return Array.from(set).filter(Boolean);
  };

  getUnitOptions = (currentVal) => {
    const units = ['KG', 'LB', 'K', 'L'];
    const set = new Set([...(currentVal ? [currentVal] : []), ...units]);
    return Array.from(set).filter(Boolean);
  };

  getContactTypeOptions = () => [
    {
      value: 'EM',
      label: 'EM - Email' 
    },
    {
      value: 'TE',
      label: 'TE - Telephone' 
    },
    {
      value: 'FX',
      label: 'FX - Fax' 
    },
    {
      value: 'TL',
      label: 'TL - Telex' 
    },
  ];

  getChgsCodeOptions = (currentVal) => {
    const codes = ['PP', 'CC', 'PX', 'CX', 'NC', 'CA', 'CB', 'CE', 'CG', 'CP', 'CZ'];
    const set = new Set([...(currentVal ? [currentVal] : []), ...codes]);
    return Array.from(set).filter(Boolean);
  };

  getChargeCodeOptions = (currentVal) => {
    const codes = ['MYC', 'SEC', 'FSC', 'AWC', 'CGC', 'TXC', 'RAC', 'INSC', 'PUC', 'DOC'];
    const set = new Set([...(currentVal ? [currentVal] : []), ...codes]);
    return Array.from(set).filter(Boolean);
  };

  getAccountingIdOptions = (currentVal) => {
    const ids = ['CRD', 'GEN', 'RET', 'MCO', 'GBL', 'GOV'];
    const set = new Set([...(currentVal ? [currentVal] : []), ...ids]);
    return Array.from(set).filter(Boolean);
  };

  getShipmentTypeOptions = (currentVal) => {
    const types = ['8471.30', 'T', 'GEN', 'PER', 'VAL', 'DGR', 'HEA', 'PIL', 'AOG'];
    const set = new Set([...(currentVal ? [currentVal] : []), ...types]);
    return Array.from(set).filter(Boolean);
  };

  handleFieldChange = (path, eOrValue) => {
    const value = eOrValue && typeof eOrValue === 'object' && eOrValue.target && eOrValue.target.value !== undefined
      ? eOrValue.target.value
      : eOrValue;
    this.setState((prevState) => {
      const baseData = prevState.data || this.props.data || {};
      let data;
      try {
        data = JSON.parse(JSON.stringify(baseData));
      } catch (err) {
        data = { ...baseData };
      }
      const keys = path.split('.');
      let current = data;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const nextKey = keys[i + 1];
        if (current[key] === undefined || current[key] === null) {
          current[key] = isNaN(Number(nextKey)) ? {} : [];
        } else if (Array.isArray(current[key])) {
          current[key] = [...current[key]];
        } else if (typeof current[key] === 'object') {
          current[key] = { ...current[key] };
        } else {
          current[key] = isNaN(Number(nextKey)) ? {} : [];
        }

        if (Array.isArray(current[key]) && !isNaN(Number(nextKey))) {
          const index = Number(nextKey);
          if (current[key][index] === undefined || current[key][index] === null || typeof current[key][index] !== 'object') {
            current[key][index] = {};
          } else {
            current[key][index] = { ...current[key][index] };
          }
        }

        current = current[key];
      }
      current[keys[keys.length - 1]] = value;
      const modifiedPaths = {
        ...(prevState.modifiedPaths || {}),
        [path]: true 
      };
      return {
        data,
        hasFormChanges: true,
        modifiedPaths,
      };
    });
  };

  handleOpen = (modal) => () => {
    this.setState({ modal });
  };
  handleClose = () => () => {
    this.setState({ modal: false });
  };

  handleRejectFormDiff = () => {
    this.setState({
      formDiffModalOpen: false,
      formDiffOldText: '',
      formDiffNewText: '',
      formDiffGuidelines: null,
      data: this.state.initialData || this.props.data,
      modifiedPaths: {},
      hasFormChanges: false,
    });
    toast.info('Changes discarded');
  };

  handleAcceptFormDiff = (anchorEl) => {
    this.setState({
      saveModalOpen: true,
      saveModalAnchorEl: anchorEl,
      saveContext: {
        source: 'manual',
        fwbToSave: this.state.formDiffNewText,
        guidelines: this.state.formDiffGuidelines,
        formData: this.state.data,
      },
    });
  };

  handleUpdateFwbFromForm = () => {
    const fwbMessage =
      this.props.fwbRawText ||
      this.state.editableFwbText ||
      (Array.isArray(this.props.fwbLines) && this.props.fwbLines.length > 0 ? this.props.fwbLines.join('\n') : '');
    const currentData = this.state.data || this.props.data || {};
    const formData = prepareFormDataForSubmission(
      currentData,
      this.state.modifiedPaths,
      fwbMessage,
      this.state.initialData || this.props.data
    );
    const payload = {
      fwbMessage,
      formData,
    };

    this.setState({ isUpdatingFwb: true });

    http
      .post('/doc_fwb/manual-form', payload)
      .then((res) => {
        const responseData = res?.data;
        if (responseData && responseData.fwbMessage) {
          const updatedFwb = responseData.fwbMessage;
          const guidelines = responseData.guidelines || null;

          this.setState({
            isUpdatingFwb: false,
            formDiffModalOpen: true,
            formDiffOldText: fwbMessage,
            formDiffNewText: updatedFwb,
            formDiffGuidelines: guidelines,
          });
        } else {
          this.setState({ isUpdatingFwb: false });
          toast.error('Unexpected response while updating FWB');
        }
      })
      .catch((err) => {
        console.error('Error updating FWB from manual form:', err);
        this.setState({ isUpdatingFwb: false });
        const errMsg = err?.response?.data?.message || err?.message || 'Failed to update FWB';
        toast.error(errMsg);
      });
  };

  handleConsoleAwbData = () => {
    const fwbMessage =
      this.props.fwbRawText ||
      this.state.editableFwbText ||
      (Array.isArray(this.props.fwbLines) && this.props.fwbLines.length > 0 ? this.props.fwbLines.join('\n') : '');
    const formData = this.state.data || this.props.data || {};
    const payload = {
      fwbMessage,
      formData,
      data: this.props.data,
    };
    console.log('[AWB Data Elements & FWB Message Payload]:', payload);
  };

  render() {
    const data = this.state.data || this.props.data;
    
    const awbElements =  <>
      <Grid container spacing={GRID_SPACING} className={classes.awbElementsContainer}>
        {/* Shipment Details & Routing & Flights */}
        <Grid item xs={12} md={4} lg={12} xl={4}>
          <Section title='Shipment Details'>
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={6} md={6} lg xl={6}>
                <FormGroup
                  id='shipment-origin'
                  label='Origin'
                  select
                  value={data?.shipmentDetails?.origin}
                  options={this.getAirportOptions(data?.shipmentDetails?.origin)}
                  onChange={(e) => this.handleFieldChange('shipmentDetails.origin', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg xl={6}>
                <FormGroup
                  id='shipment-destination'
                  label='Destination'
                  select
                  value={data?.shipmentDetails?.destination}
                  options={this.getAirportOptions(data?.shipmentDetails?.destination)}
                  onChange={(e) => this.handleFieldChange('shipmentDetails.destination', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg xl={4}>
                <FormGroup
                  label='Pieces'
                  value={data?.shipmentDetails?.pieces}
                  onChange={(e) => this.handleFieldChange('shipmentDetails.pieces', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg xl={4}>
                <FormGroup
                  label='Weight'
                  value={data?.shipmentDetails?.weight}
                  onChange={(e) => this.handleFieldChange('shipmentDetails.weight', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg xl={4}>
                <FormGroup
                  id='shipment-unit'
                  label='Unit'
                  select
                  value={data?.shipmentDetails?.unit}
                  options={this.getUnitOptions(data?.shipmentDetails?.unit)}
                  onChange={(e) => this.handleFieldChange('shipmentDetails.unit', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Routing & Flights */}
        <Grid item xs={12} md={8} lg={12} xl={8}>
          <Section title={
            <Stack direction='row' justifyContent='space-between' spacing={GRID_SPACING}>
              <Typography variant='inherit' component='span'>Routing & Flights</Typography>
              <AddIcon title={'Routing & Flights'} />
            </Stack>}>
              
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  id='routing-0-to'
                  label='To'
                  select
                  value={data?.routingAndFlights?.[0]?.to}
                  options={this.getAirportOptions(data?.routingAndFlights?.[0]?.to)}
                  onChange={(e) => this.handleFieldChange('routingAndFlights.0.to', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  id='routing-0-by-carrier'
                  label='By Carrier'
                  select
                  value={data?.routingAndFlights?.[0]?.byCarrier}
                  options={this.getCarrierOptions(data?.routingAndFlights?.[0]?.byCarrier)}
                  onChange={(e) => this.handleFieldChange('routingAndFlights.0.byCarrier', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  label='Flight Number'
                  value={data?.routingAndFlights?.[0]?.flightNumber}
                  onChange={(e) => this.handleFieldChange('routingAndFlights.0.flightNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  label='Date'
                  date
                  value={data?.routingAndFlights?.[0]?.date}
                  onChange={(e) => this.handleFieldChange('routingAndFlights.0.date', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  id='routing-1-to'
                  label='To'
                  select
                  value={data?.routingAndFlights?.[1]?.to}
                  options={this.getAirportOptions(data?.routingAndFlights?.[1]?.to)}
                  onChange={(e) => this.handleFieldChange('routingAndFlights.1.to', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  id='routing-1-by-carrier'
                  label='By Carrier'
                  select
                  value={data?.routingAndFlights?.[1]?.byCarrier}
                  options={this.getCarrierOptions(data?.routingAndFlights?.[1]?.byCarrier)}
                  onChange={(e) => this.handleFieldChange('routingAndFlights.1.byCarrier', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  label='Flight Number'
                  value={data?.routingAndFlights?.[1]?.flightNumber}
                  onChange={(e) => this.handleFieldChange('routingAndFlights.1.flightNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  label='Date'
                  date
                  value={data?.routingAndFlights?.[1]?.date}
                  onChange={(e) => this.handleFieldChange('routingAndFlights.1.date', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Shipper */}
        <Grid item xs={12}>
          <Section title='Shipper'>
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={6} md={3} lg={6} xl={4}>
                <FormGroup
                  label='Name'
                  value={data?.shipper?.name}
                  onChange={(e) => this.handleFieldChange('shipper.name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={6} xl={4}>
                <FormGroup
                  label='Address'
                  value={data?.shipper?.address}
                  onChange={(e) => this.handleFieldChange('shipper.address', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={6} xl={2}>
                <FormGroup
                  label='City / Place'
                  value={data?.shipper?.cityPlace}
                  onChange={(e) => this.handleFieldChange('shipper.cityPlace', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={6} xl={2}>
                <FormGroup
                  label='State / Province'
                  value={data?.shipper?.stateProvince}
                  onChange={(e) => this.handleFieldChange('shipper.stateProvince', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={2.5}>
                <FormGroup
                  id='shipper-country'
                  label='Country'
                  select
                  value={data?.shipper?.country}
                  options={this.getCountryOptions(data?.shipper?.country)}
                  onChange={(e) => this.handleFieldChange('shipper.country', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={1.5}>
                <FormGroup
                  label='Postal / Zip Code'
                  value={data?.shipper?.postalZipCode}
                  onChange={(e) => this.handleFieldChange('shipper.postalZipCode', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={1.5}>
                <FormGroup
                  id='shipper-contact-type'
                  label='Contact Type'
                  select
                  value={data?.shipper?.contactType}
                  options={this.getContactTypeOptions()}
                  onChange={(e) => this.handleFieldChange('shipper.contactType', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={2.5}>
                <FormGroup
                  label='Contact Number'
                  value={data?.shipper?.contactNumber}
                  onChange={(e) => this.handleFieldChange('shipper.contactNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={4}>
                <FormGroup
                  label='Shippers Account'
                  value={data?.shipper?.shippersAccount}
                  onChange={(e) => this.handleFieldChange('shipper.shippersAccount', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Consignee */}
        <Grid item xs={12}>
          <Section title='Consignee'>
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={6} md={3} lg={6} xl={4}>
                <FormGroup
                  label='Name'
                  value={data?.consignee?.name}
                  onChange={(e) => this.handleFieldChange('consignee.name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={6} xl={4}>
                <FormGroup
                  label='Address'
                  value={data?.consignee?.address}
                  onChange={(e) => this.handleFieldChange('consignee.address', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={6} xl={2}>
                <FormGroup
                  label='City / Place'
                  value={data?.consignee?.cityPlace}
                  onChange={(e) => this.handleFieldChange('consignee.cityPlace', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={6} xl={2}>
                <FormGroup
                  label='State / Province'
                  value={data?.consignee?.stateProvince}
                  onChange={(e) => this.handleFieldChange('consignee.stateProvince', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={2.5}>
                <FormGroup
                  id='consignee-country'
                  label='Country'
                  select
                  value={data?.consignee?.country}
                  options={this.getCountryOptions(data?.consignee?.country)}
                  onChange={(e) => this.handleFieldChange('consignee.country', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={1.5}>
                <FormGroup
                  label='Postal / Zip Code'
                  value={data?.consignee?.postalZipCode}
                  onChange={(e) => this.handleFieldChange('consignee.postalZipCode', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={1.5}>
                <FormGroup
                  id='consignee-contact-type'
                  label='Contact Type'
                  select
                  value={data?.consignee?.contactType}
                  options={this.getContactTypeOptions()}
                  onChange={(e) => this.handleFieldChange('consignee.contactType', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={2.5}>
                <FormGroup
                  label='Contact Number'
                  value={data?.consignee?.contactNumber}
                  onChange={(e) => this.handleFieldChange('consignee.contactNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4} lg={3} xl={4}>
                <FormGroup
                  label="Consignee's Account"
                  value={data?.consignee?.consigneesAccount}
                  onChange={(e) => this.handleFieldChange('consignee.consigneesAccount', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Issuing Carriers Agent & Accounting Information */}

        <Grid item xs={12} md={6} lg={12} xl={6}>
          <Section title='Issuing Carriers Agent'>
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FormGroup
                  label='Name'
                  value={data?.issuingCarriersAgent?.name}
                  onChange={(e) => this.handleFieldChange('issuingCarriersAgent.name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <FormGroup
                  label='City'
                  value={data?.issuingCarriersAgent?.city}
                  onChange={(e) => this.handleFieldChange('issuingCarriersAgent.city', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <FormGroup
                  label='IATA Code'
                  value={data?.issuingCarriersAgent?.iataCode}
                  onChange={(e) => this.handleFieldChange('issuingCarriersAgent.iataCode', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <FormGroup
                  label='Account Number'
                  value={data?.issuingCarriersAgent?.accountNumber}
                  onChange={(e) => this.handleFieldChange('issuingCarriersAgent.accountNumber', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12} md={6} lg={12} xl={6}>
          <Section title={<Stack direction='row' justifyContent='space-between' spacing={GRID_SPACING}>
            <Typography variant='inherit' component='span'>Accounting Information</Typography>
            <AddIcon title={'Accounting Information'} onClick={this.handleOpen('accountingInformation')} />
          </Stack>}>  
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <FormGroup
                  id='accounting-info-0-identifier'
                  label='Identifier'
                  select
                  value={data?.accountingInformation?.[0]?.identifier}
                  options={this.getAccountingIdOptions(data?.accountingInformation?.[0]?.identifier)}
                  onChange={(e) => this.handleFieldChange('accountingInformation.0.identifier', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8} lg={8} xl={8}>
                <FormGroup
                  label='Information'
                  value={data?.accountingInformation?.[0]?.information}
                  onChange={(e) => this.handleFieldChange('accountingInformation.0.information', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <FormGroup
                  id='accounting-info-1-identifier'
                  select
                  value={data?.accountingInformation?.[1]?.identifier}
                  options={this.getAccountingIdOptions(data?.accountingInformation?.[1]?.identifier)}
                  onChange={(e) => this.handleFieldChange('accountingInformation.1.identifier', e.target.value)}
                  ariaLabel='Accounting information identifier'
                />
              </Grid>
              <Grid item xs={12} md={8} lg={8} xl={8}>
                <FormGroup
                  value={data?.accountingInformation?.[1]?.information}
                  onChange={(e) => this.handleFieldChange('accountingInformation.1.information', e.target.value)}
                  ariaLabel='Accounting information details'
                  id='accounting-information-details-2'
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>
        {/* Charge Declaration */}
        <Grid item xs={12}>
          <Section title='Charge Declaration'>
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={6} md={2} lg={4} xl>
                <FormGroup
                  id='charge-currency'
                  label='Currency'
                  select
                  value={data?.chargeDeclaration?.currency}
                  options={this.getCurrencyOptions(data?.chargeDeclaration?.currency)}
                  onChange={(e) => this.handleFieldChange('chargeDeclaration.currency', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={4} xl='auto'>
                <InputRadioGroup
                  label='Weight/Valuation Charge'
                  name='wvCharge'
                  value={data?.chargeDeclaration?.pcWeightVal || ''}
                  onChange={(e) => this.handleFieldChange('chargeDeclaration.pcWeightVal', e.target.value)}
                  options={[
                    {
                      value: 'P',
                      label: 'Prepaid',
                    },
                    {
                      value: 'C',
                      label: 'Collect',
                    },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={4} xl='auto'>
                <InputRadioGroup
                  label='Other Charges'
                  name='oCharge'
                  value={data?.chargeDeclaration?.pcOther || ''}
                  onChange={(e) => this.handleFieldChange('chargeDeclaration.pcOther', e.target.value)}
                  options={[
                    {
                      value: 'P',
                      label: 'Prepaid',
                    },
                    {
                      value: 'C',
                      label: 'Collect',
                    },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={4} xl={2}>
                <FormGroup
                  label='Declared Value for Carriage'
                  value={data?.chargeDeclaration?.declaredValueForCarriage}
                  onChange={(e) => this.handleFieldChange('chargeDeclaration.declaredValueForCarriage', e.target.value)}
                  wrapperStyle={{ flex: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={4} xl={2}>
                <FormGroup
                  label='Declared Value for Customs'
                  value={data?.chargeDeclaration?.declaredValueForCustoms}
                  onChange={(e) => this.handleFieldChange('chargeDeclaration.declaredValueForCustoms', e.target.value)}
                  wrapperStyle={{ flex: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={4} xl={2}>
                <FormGroup
                  label='Amount of Insurance'
                  value={data?.chargeDeclaration?.amountOfInsurance}
                  onChange={(e) => this.handleFieldChange('chargeDeclaration.amountOfInsurance', e.target.value)}
                  wrapperStyle={{ flex: 1.5 }}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Rate Description */}
        <Grid item xs={12}>
          <Section title={<Stack direction='row' justifyContent='space-between' spacing={GRID_SPACING}>
            <Typography variant='inherit' component='span'>Rate Description</Typography>
            <AddIcon title={'Rate Description'} />
          </Stack>}>  
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={1} md={1} lg={1}xl='auto'>1</Grid>
              <Grid item xs={12} sm={1} md={1} lg={3} xl={1}>
                <FormGroup
                  label='Pcs'
                  value={data?.ratingDetails?.piecesRcp}
                  onChange={(e) => this.handleFieldChange('ratingDetails.piecesRcp', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={4} xl>
                <Stack direction='row' spacing={1}>
                  <FormGroup
                    label='Gross Weight'
                    value={data?.ratingDetails?.grossWeight}
                    onChange={(e) => this.handleFieldChange('ratingDetails.grossWeight', e.target.value)}
                    wrapperStyle={{ flex: 1 }}
                  />
                  <FormGroup
                    sx={{ alignSelf: 'flex-end' }}
                    select
                    value={data?.ratingDetails?.weightUnit || data?.shipmentDetails?.unit || 'KG'}
                    options={this.getUnitOptions(data?.ratingDetails?.weightUnit || data?.shipmentDetails?.unit)}
                    onChange={(e) => this.handleFieldChange('ratingDetails.weightUnit', e.target.value)}
                    ariaLabel='Gross weight unit'
                    id='rating-details-weight-unit'
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={4} xl>
                <FormGroup
                  label='Rate Class'
                  value={data?.ratingDetails?.rateClass}
                  onChange={(e) => this.handleFieldChange('ratingDetails.rateClass', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={4} xl={1.5}>
                <FormGroup
                  label='Commodity Item#'
                  value={data?.ratingDetails?.commodityItemNo}
                  onChange={(e) => this.handleFieldChange('ratingDetails.commodityItemNo', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4} xl={2}>
                <Stack direction='row' spacing={1}>
                  <Box component='div' display='flex'>
                    <div className={classes.unitTag}>W</div>
                  </Box>
                  <FormGroup
                    label='Chargeable weight'
                    value={data?.ratingDetails?.chargeableWeight !== undefined && data?.ratingDetails?.chargeableWeight !== null ? data.ratingDetails.chargeableWeight : ''}
                    onChange={(e) => this.handleFieldChange('ratingDetails.chargeableWeight', e.target.value)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4} xl={2}>
                <Stack direction='row' spacing={1}>
                  <Box component='div' display='flex'>
                    <div className={classes.unitTag}>R</div>
                  </Box>
                  <FormGroup
                    label='Rate'
                    value={data?.ratingDetails?.rateCharge !== undefined && data?.ratingDetails?.rateCharge !== null ? data.ratingDetails.rateCharge : ''}
                    onChange={(e) => this.handleFieldChange('ratingDetails.rateCharge', e.target.value)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4} xl={2}>
                <Stack direction='row' spacing={1}>
                  <Box component='div' display='flex'>
                    <div className={classes.unitTag}>T</div>
                  </Box>
                  <FormGroup
                    label='Total Details'
                    value={data?.ratingDetails?.totalCharge !== undefined && data?.ratingDetails?.totalCharge !== null ? data.ratingDetails.totalCharge : ''}
                    onChange={(e) => this.handleFieldChange('ratingDetails.totalCharge', e.target.value)}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Section>
        </Grid>
        <Grid item container xs={12} spacing={GRID_SPACING}>
          <Grid item container xs={12} md={6} lg={12} xl={6} spacing={GRID_SPACING}>
            {/* Nature and Quantity vs (Other Charges + Charge Summary) */}
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Section title='Nature and Quantity'>
                <Grid container spacing={GRID_SPACING}>
                  <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                    <FormGroup
                      id='rating-shipment-type'
                      label='Shipment Type'
                      select
                      value={data?.ratingDetails?.harmonisedCommodityCode || '8471.30'}
                      options={this.getShipmentTypeOptions(data?.ratingDetails?.harmonisedCommodityCode)}
                      onChange={(e) => this.handleFieldChange('ratingDetails.harmonisedCommodityCode', e.target.value)}
                      wrapperStyle={{ flex: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                    <FormGroup
                      id='rating-country-of-origin'
                      label='Country of Origin'
                      select
                      value={data?.ratingDetails?.countryOfOrigin}
                      options={this.getCountryOptions(data?.ratingDetails?.countryOfOrigin)}
                      onChange={(e) => this.handleFieldChange('ratingDetails.countryOfOrigin', e.target.value)}
                      wrapperStyle={{ flex: 1 }}
                    />
                  </Grid>
            
                  <Grid item xs={12} sm={6} md={6} lg={12} xl={12}>
                    <FormGroup
                      label={<Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <span>Nature of Goods</span>
                        <AddIcon title={'Nature of Goods'} />
                      </Stack>}
                      value={data?.ratingDetails?.natureOfGoods?.[0] || ''}
                      onChange={(e) => this.handleFieldChange('ratingDetails.natureOfGoods.0', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                    <InputRadioGroup
                      label='Dimensions'
                      name='dimUnit'
                      value={data?.ratingDetails?.dimensions?.unit || ''}
                      onChange={(e) => this.handleFieldChange('ratingDetails.dimensions.unit', e.target.value)}
                      options={[
                        {
                          value: 'CM',
                          label: 'CM',
                        },
                        {
                          value: 'IN',
                          label: 'IN',
                        },
                      ]}
                    />
                  </Grid>
                  <Grid item container spacing={GRID_SPACING} xs={12} sm={6} md={8} lg={10} xl={10}>
                    <Grid item xs={12} sm={6} md={2.4} lg xl>
                      <FormGroup
                        label='Length'
                        value={data?.ratingDetails?.dimensions?.measurements?.[0]?.length}
                        onChange={(e) => this.handleFieldChange('ratingDetails.dimensions.measurements.0.length', e.target.value)}
                        wrapperStyle={{ flex: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4} lg xl>
                      <FormGroup
                        label='Width'
                        value={data?.ratingDetails?.dimensions?.measurements?.[0]?.width}
                        onChange={(e) => this.handleFieldChange('ratingDetails.dimensions.measurements.0.width', e.target.value)}
                        wrapperStyle={{ flex: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4} lg xl>
                      <FormGroup
                        label='Height'
                        value={data?.ratingDetails?.dimensions?.measurements?.[0]?.height}
                        onChange={(e) => this.handleFieldChange('ratingDetails.dimensions.measurements.0.height', e.target.value)}
                        wrapperStyle={{ flex: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4} lg xl>
                      <FormGroup
                        label='Pieces'
                        value={data?.ratingDetails?.dimensions?.measurements?.[0]?.pieces !== undefined && data?.ratingDetails?.dimensions?.measurements?.[0]?.pieces !== null ? data.ratingDetails.dimensions.measurements[0].pieces : ''}
                        onChange={(e) => this.handleFieldChange('ratingDetails.dimensions.measurements.0.pieces', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4} lg='auto' xl='auto'>
                      <AddIcon />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
                    <FormGroup
                      label='Volume'
                      value={data?.ratingDetails?.dimensions?.measurements?.[0]?.volume}
                      onChange={(e) => this.handleFieldChange('ratingDetails.dimensions.measurements.0.volume', e.target.value)}
                      wrapperStyle={{ flex: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8} lg xl>
                    <FormGroup
                      label='ULD Information'
                      value={data?.ratingDetails?.uldInformation?.[0] || ''}
                      onChange={(e) => this.handleFieldChange('ratingDetails.uldInformation.0', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={8} lg={'auto'} xl={'auto'}>
                    <AddIcon title={'ULD Information'} />
                  </Grid>
                  <Grid item xs={12} md={4} lg={2} xl={2}>
                    <FormGroup
                      label='SLAC'
                      value={data?.ratingDetails?.slac}
                      onChange={(e) => this.handleFieldChange('ratingDetails.slac', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Section>
            </Grid>
            {/* Shippers Certification & Special Service Request */}
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Section title='Shippers Certification'>
                <FormGroup
                  label='Shipper / Agents Name'
                  value={data?.shippersCertification?.shipperAgentsName}
                  onChange={(e) => this.handleFieldChange('shippersCertification.shipperAgentsName', e.target.value)}
                />
              </Section>
            </Grid>
          </Grid>

          <Grid item container xs={12} md={6} lg={12} xl={6} spacing={GRID_SPACING}>
            {/* Other Charges */}
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Section title={<Stack direction='row' justifyContent='space-between' spacing={GRID_SPACING}>
                <Typography variant='inherit' component='span'>Other Charges</Typography>
                <AddIcon title={'Other Charges'} />
              </Stack>}>  
                <Grid container spacing={GRID_SPACING}>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <FormGroup
                      id='other-charges-0-pc'
                      label='P/C'
                      select
                      value={data?.otherCharges?.[0]?.pc}
                      options={['P', 'C']}
                      onChange={(e) => this.handleFieldChange('otherCharges.0.pc', e.target.value)}
                      wrapperStyle={{ flex: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <FormGroup
                      id='other-charges-0-code'
                      label='Code'
                      select
                      value={data?.otherCharges?.[0]?.code}
                      options={this.getChargeCodeOptions(data?.otherCharges?.[0]?.code)}
                      onChange={(e) => this.handleFieldChange('otherCharges.0.code', e.target.value)}
                      wrapperStyle={{ flex: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <FormGroup
                      id='other-charges-0-due-to'
                      label='Due to'
                      select
                      value={data?.otherCharges?.[0]?.dueTo}
                      options={['C', 'A']}
                      onChange={(e) => this.handleFieldChange('otherCharges.0.dueTo', e.target.value)}
                      wrapperStyle={{ flex: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <FormGroup
                      id='other-charges-0-amount'
                      label='Amount'
                      value={data?.otherCharges?.[0]?.amount}
                      onChange={(e) => this.handleFieldChange('otherCharges.0.amount', e.target.value)}
                      wrapperStyle={{ flex: 1.5 }}
                    />
                  </Grid>
                </Grid>
              </Section>

            </Grid>
            {/* Charge Summary*/}
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Section title='Charge Summary'>
                <Grid container className={classes.chargeSummaryRow} spacing={GRID_SPACING}>
                  <Grid item container xs={12} md={12} lg={12} xl={12} spacing={GRID_SPACING}>
                    <Grid item xs={12} md={4} lg={4} xl={4}>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4} xl={4}>
                      <Typography className={classes.chargeSummaryTab}>
                          Prepaid
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4} xl={4}>
                      <Typography className={classes.chargeSummaryTab}>
                          Collect
                      </Typography>
                    </Grid>
                  </Grid>
            
                  {[
                    {
                      label: 'Weight Charge',
                      key: 'weightCharge',
                    },
                    {
                      label: 'Valuation Charge',
                      key: 'valuationCharge',
                    },
                    {
                      label: 'Tax',
                      key: 'tax',
                    },
                    {
                      label: 'Total Other Charges Due Agent',
                      key: 'totalOtherChargesDueAgent',
                    },
                    {
                      label: 'Total Other Charges Due Carrier',
                      key: 'totalOtherChargesDueCarrier',
                    },
                    {
                      label: <strong key='total'>Total</strong>,
                      key: 'total',
                    },
                  ].map((item, idx) => {
                    const chargeItem = data?.chargeSummary?.[item.key] || {};
                    return (
                      <Grid item xs={12} md={12} lg={12} xl={12} container key={idx} spacing={GRID_SPACING} className={classes.chargeSummaryRow}>
                        <Grid item xs={12} md={4} lg={4} xl={4}>
                          <div className={classes.chargeLabel}>{item.label}</div>
                        </Grid>
                        <Grid item xs={12} md={4} lg={4} xl={4}>
                          <FormGroup
                            value={chargeItem.prepaid !== null && chargeItem.prepaid !== undefined ? chargeItem.prepaid : ''}
                            onChange={(e) => this.handleFieldChange(`chargeSummary.${item.key}.prepaid`, e.target.value)}
                            ariaLabel={`${item.key} prepaid`}
                            id={`charge-summary-${item.key}-prepaid`}
                          />
                        </Grid>
                        <Grid item xs={12} md={4} lg={4} xl={4}>
                          <FormGroup
                            value={chargeItem.collect !== null && chargeItem.collect !== undefined ? chargeItem.collect : ''}
                            onChange={(e) => this.handleFieldChange(`chargeSummary.${item.key}.collect`, e.target.value)}
                            ariaLabel={`${item.key} collect`}
                            id={`charge-summary-${item.key}-collect`}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </Section>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} container spacing={GRID_SPACING}>
          {/* Other Service Information */}
          <Grid item xs={12} md={6} lg={12} xl={6}>
            <Section title='Other Service Information' className={styles.h100}>
              <Stack direction='column' spacing={GRID_SPACING}>
                <FormGroup
                  label='OSI - 1'
                  value={data?.otherServiceInformation?.osi1}
                  onChange={(e) => this.handleFieldChange('otherServiceInformation.osi1', e.target.value)}
                />
                <FormGroup
                  label='OSI - 2'
                  value={data?.otherServiceInformation?.osi2}
                  onChange={(e) => this.handleFieldChange('otherServiceInformation.osi2', e.target.value)}
                />
                <FormGroup
                  label='OSI 3'
                  value={data?.otherServiceInformation?.osi3}
                  onChange={(e) => this.handleFieldChange('otherServiceInformation.osi3', e.target.value)}
                />
              </Stack>
            </Section>
          </Grid>
          <Grid item container xs={12} md={6} lg={12} xl={6} spacing={GRID_SPACING}>
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Section title={<Stack direction='row' justifyContent='space-between' spacing={GRID_SPACING}>
                <Typography variant='inherit' component='span'>Special Service Request</Typography>
                <AddIcon title={'Special Service Request'} />
              </Stack>}>  
                <FormGroup
                  label='Text'
                  value={data?.specialServiceRequest?.[0]?.text}
                  onChange={(e) => this.handleFieldChange('specialServiceRequest.0.text', e.target.value)}
                />
              </Section>
            </Grid>

            {/* Special Handling Codes */}
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Section title='Special Handling Codes'>
                <Grid container spacing={GRID_SPACING}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((idx) => (
                    <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4} key={idx}>
                      <FormGroup
                        value={data?.specialHandlingCodes?.[idx] || ''}
                        onChange={(e) => this.handleFieldChange(`specialHandlingCodes.${idx}`, e.target.value)}
                        ariaLabel={`Special handling code ${idx + 1}`}
                        id={`special-handling-code-${idx + 1}`}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Section>
            </Grid>
          </Grid>
          
        </Grid>

        {/* Other Customs / Security Information */}
        <Grid item xs={12}>
          <Section title={<Stack direction='row' justifyContent='space-between' spacing={GRID_SPACING}>
            <Typography variant='inherit' component='span'>Other Customs / Security Information</Typography>
            <AddIcon title={'Other Customs / Security Information'} />
          </Stack>}>  
            <Grid container spacing={GRID_SPACING}>
              <Grid item xs={12} sm={6} md={2} lg={2} xl={2}>
                <FormGroup
                  id='oci-0-country-code'
                  label='Country Code'
                  select
                  value={data?.otherCustomsSecurityInformation?.[0]?.countryCode}
                  options={this.getCountryOptions(data?.otherCustomsSecurityInformation?.[0]?.countryCode)}
                  onChange={(e) => this.handleFieldChange('otherCustomsSecurityInformation.0.countryCode', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  label='Information Type'
                  value={data?.otherCustomsSecurityInformation?.[0]?.informationType}
                  onChange={(e) => this.handleFieldChange('otherCustomsSecurityInformation.0.informationType', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <FormGroup
                  label='Information Code'
                  value={data?.otherCustomsSecurityInformation?.[0]?.informationCode}
                  onChange={(e) => this.handleFieldChange('otherCustomsSecurityInformation.0.informationCode', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={4} xl={4}>
                <FormGroup
                  label='Information'
                  value={data?.otherCustomsSecurityInformation?.[0]?.information}
                  onChange={(e) => this.handleFieldChange('otherCustomsSecurityInformation.0.information', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>
    </>;

    const fwbVersions = this.props.fwbVersions || [];
    const selectedVersionNum = this.props.selectedFwbVersion || 1;
    const currentVersionObj = fwbVersions.find((v) => v.version === selectedVersionNum) || {
      version: selectedVersionNum,
      name: `Version ${selectedVersionNum}`,
    };
    const maxVerNum = fwbVersions.reduce((max, v) => Math.max(max, v.version || 0), 0);
    const nextVerNum = (maxVerNum || selectedVersionNum || 1) + 1;

    const fwbDisplayLines = (this.state.editableFwbText || '').split(/\r?\n/);
    const lineCount = Math.max(1, fwbDisplayLines.length);

    const rawWarnings = this.props.warnings || this.props.fwbWarnings || this.props.data?.warnings || this.props.data?.fwb_warnings || [];
    const tactIssues = this.props.tactIssues || this.props.data?.tactIssues || [];

    // Deduplicate: exclude warnings already represented in tactIssues
    const generalWarnings = rawWarnings.filter((warn) => {
      if (!warn) return false;
      const isMatch = tactIssues.some((tact) => {
        if (tact.code && warn.code && tact.code === warn.code && Number(tact.line) === Number(warn.line)) {
          return true;
        }
        if (tact.line && warn.line && Number(tact.line) === Number(warn.line) && tact.field && warn.field && tact.field === warn.field) {
          return true;
        }
        if (tact.message && warn.message && tact.message.trim() === warn.message.trim()) {
          return true;
        }
        if ((warn.category === 'TACT' || warn.code?.startsWith('TACT')) && (Number(tact.line) === Number(warn.line) || tact.code === warn.code)) {
          return true;
        }
        return false;
      });
      return !isMatch;
    });

    const totalAlertsCount = (tactIssues?.length || 0) + (generalWarnings?.length || 0);
    const hasAcceptedTactIssue = Boolean(
      tactIssues &&
        tactIssues.some((issue, idx) => {
          const key = this.getIssueKey(issue, idx);
          return this.state.alertDecisions[key] === 'accept';
        })
    );

    const fwbData = (
      <>
        <Group
          id='fwb-preview-split'
          className={classes.fwbPreviewGroup}
          orientation={this.state.isStackedPreview ? 'vertical' : 'horizontal'}
          elementRef={this.handlePreviewGroupElement}
          defaultLayout={{
            fwb: 50,
            alerts: 50,
          }}
        >
          <Panel id='fwb' className={classes.fwbPreviewPanel} defaultSize={50} minSize={240}>
            <Section
              className={classes.fwbSectionCard}
              title={
                <Stack direction='row' rowGap={1} justifyContent='space-between' alignItems='center' width='100%' flexWrap='wrap'>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Typography variant='inherit' component='span'>FWB</Typography>
                    {this.props.fwbGenerated && (
                      <>
                        <button
                          type='button'
                          className={classes.versionSelectorBtn}
                          onClick={(e) => this.setState({ versionPopoverAnchorEl: e.currentTarget })}
                          title='Select FWB Version'
                        >
                          <span>{currentVersionObj.name || `Version ${currentVersionObj.version}`}</span>
                          <KeyboardArrowDown sx={{ fontSize: 16 }} />
                        </button>

                        <Button
                          size='small'
                          variant='contained'
                          color='secondary'
                          className={styles.historyIcon}
                          onClick={(e) => this.setState({ historyPopoverAnchorEl: e.currentTarget })}
                          title='View FWB change history & audit log'
                          disableElevation
                        >
                        H
                        </Button>
                      </>
                    )}
                  </Stack>

                  <CustomButton
                    variant='contained'
                    color='primary'
                    size='small'
                    sx={{
                      py: this.props.fwbGenerated ? '3px' : '6px',
                      px: 2,
                      minWidth: '100px',
                      textAlign: 'center',
                    }}
                    onClick={this.props.onGenerateFwb}
                    disabled={this.props.isGeneratingFwb}
                  >
                    {this.props.isGeneratingFwb ? (
                      <span className={classes.btnLoaderContent}>
                        <CircularProgress size={14} sx={{
                          color: '#fff',
                          mr: 1 
                        }} />
                        Generating...
                      </span>
                    ) : this.props.fwbGenerated ? (
                      <Stack direction='column' alignItems='center'>
                        <Typography component='div' variant='body2' fontSize='0.85rem' fontWeight={600} lineHeight='1.2'>
                          Regenerate FWB
                        </Typography>
                        <Typography component='span' variant='caption' fontSize='9px' color='#c8c8c8' lineHeight='normal'>
                          Last generated {this.props.lastGeneratedDate || '15MAY26'}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography component='div' variant='body2' fontSize='0.85rem' fontWeight={600}>
                        Generate FWB
                      </Typography>
                    )}
                  </CustomButton>
                </Stack>
              }
            >
              <div className={classes.fwbMessage} role='region' aria-label='FWB message'>
                {this.props.isFwbLoading ? (
                  <div className={classes.fwbLoadingState}>
                    <CircularProgress size={28} sx={{ color: 'var(--primaryblue)' }} />
                    <Typography variant='body2' sx={{
                      mt: 1.5,
                      color: '#555' 
                    }}>
                    Fetching FWB data...
                    </Typography>
                  </div>
                ) : this.props.fwbGenerated ? (
                  <div className={classes.fwbEditorContainer}>
                    <div ref={this.gutterRef} className={classes.fwbGutter}>
                      {Array.from({ length: lineCount }, (_, i) => (
                        <div key={i + 1} style={{ minWidth: `${Math.max(2, String(lineCount).length)}ch` }}>
                          {i + 1}
                        </div>
                      ))}
                    </div>
                    <textarea
                      className={classes.fwbTextarea}
                      value={this.props.fwbRawText || this.state.editableFwbText || ''}
                      readOnly={true}
                      onScroll={this.handleTextareaScroll}
                      spellCheck={false}
                      autoCapitalize='characters'
                    />
                  </div>
                ) : (
                  <div className={classes.fwbEmptyState}>
                    <Typography className={classes.fwbEmptyText}>
                    No FWB Generated Yet
                    </Typography>
                  </div>
                )}
              </div>

              {this.state.isManualEditing && (
                <div className={classes.fwbBottomActionBar}>
                  <CustomButton
                    variant='outlined'
                    size='small'
                    onClick={this.handleCancelManualEdit}
                  >
                  Cancel
                  </CustomButton>
                  <CustomButton
                    variant='contained'
                    color='primary'
                    size='small'
                    onClick={this.handleOpenManualSave}
                  >
                  Save
                  </CustomButton>
                </div>
              )}
            </Section>
          </Panel>
          <Separator
            className={classes.fwbPreviewSeparator}
            aria-label={this.state.isStackedPreview ? 'Resize FWB and alerts heights' : 'Resize FWB and alerts widths'}
          />
          <Panel id='alerts' className={classes.fwbPreviewPanel} defaultSize={50} minSize={240}>
            <Section title=''
              className={classes.fwbSectionCard}>
              <Tabs
                value={this.state.rightSectionView}
                onChange={this.handleTabChange}
                className={`${styles.customTabs}`}
                variant='fullWidth'
                indicatorColor='transparent'
                aria-label='Alerts and CABA AI views'
                sx={{
                  mb:2,
                  justifyContent: 'center',
                  '& .MuiTab-root': { '&:not(.Mui-selected)': { background: '#c7c7c7', }, },
                }}
                classes={{ scroller: styles.tabFlexContainer }}
                ScrollButtonComponent={(props) => {
                  if (
                    props.direction === 'left'
                  ) {
                    return (
                      <IconButton {...props} className={styles.tabArrows} disableRipple size='small'>
                        <ArrowLeftIcon fontSize='inherit' 
                          sx={{ color: props.disabled ? 'disabled' : theme.palette.primary.main }}
                        />
                      </IconButton>
                    );
                  } else if (
                    props.direction === 'right'
                  ) {
                    return (
                      <IconButton {...props} className={styles.tabArrows} disableRipple>
                        <ArrowRightIcon
                          sx={{ color: props.disabled ? 'disabled' : theme.palette.primary.main }}
                        />
                      </IconButton>
                    );
                  } else {
                    return null;
                  }
                }}
              >
                <CustomTab className={classes.alertTab} label={<Stack direction='row'spacing={2}>
                  <Typography variant='inherit'>Alerts</Typography>
                  <Chip label={totalAlertsCount} className={classes.alertChip} classes={{ label: classes.alertChipLabel }} color='error' size='small' />
                </Stack>} 
                value='alerts' />
                <CustomTab label='CABA AI' value='chatbot' />
              </Tabs>
              {this.state.rightSectionView === 'chatbot' && (
                <div className={classes.chatbotContainer}>
                  <div className={classes.chatMessagesContainer} ref={this.chatMessagesContainerRef}>
                    {this.state.chatMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={msg.sender === 'user' ? classes.userMessageRow : classes.aiMessageRow}
                      >
                        <div className={msg.sender === 'user' ? classes.userMessageBubble : classes.aiMessageBubble} style={msg.isDiff ? {
                          maxWidth: '96%',
                          width: '96%' 
                        } : {}}>
                          <div className={msg.sender === 'user' ? classes.userMessageText : classes.aiMessageText}>
                            {msg.text}
                          </div>
                          {msg.isDiff && (
                            <FwbDiffViewer
                              oldText={msg.oldFwb}
                              newText={msg.refinedFwb}
                              guidelines={msg.guidelines}
                              modelId={msg.modelId}
                              status={msg.diffStatus || 'pending'}
                              sideBySide={false}
                              showViewToggle={false}
                              onAccept={(e) => this.handleOpenAiSave(msg, e?.currentTarget)}
                              onReject={() => this.handleRejectAiDiff(msg.id)}
                            />
                          )}
                          <div className={msg.sender === 'user' ? classes.userMessageMeta : classes.aiMessageMeta}>
                            <span>{msg.sender === 'user' ? 'You' : 'CABA AI'}</span>
                            <span>•</span>
                            <span>{msg.time}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {this.state.isAiResponding && (
                      <div className={classes.aiMessageRow}>
                        <div className={classes.aiMessageBubble}>
                          <div className={classes.aiLoadingDots}>
                            <span className={classes.aiLoadingDot} />
                            <span className={classes.aiLoadingDot} />
                            <span className={classes.aiLoadingDot} />
                          </div>
                          <div className={classes.aiMessageMeta}>
                            <span>CABA AI is processing changes...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={this.chatBottomRef} />
                  </div>

                  <div className={classes.chatInputWrapper}>
                    <div className={classes.chatInputBar}>
                      <input
                        type='text'
                        className={classes.chatInputField}
                        placeholder='Ask CABA AI about this AWB or request changes...'
                        value={this.state.chatInputText}
                        onChange={(e) => this.setState({ chatInputText: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            this.handleSendChatMessage();
                          }
                        }}
                        disabled={this.state.isAiResponding}
                      />
                      <button
                        type='button'
                        className={classes.chatSendButton}
                        onClick={this.handleSendChatMessage}
                        disabled={!this.state.chatInputText.trim() || this.state.isAiResponding}
                        title='Send prompt'
                      >
                        <ArrowUpward sx={{ fontSize: 18 }} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {this.state.rightSectionView === 'alerts' && (
                <div className={classes.alertsListContainer}>
                  <Stack direction='column' spacing={3}>
                    {tactIssues && tactIssues.length > 0 &&
                      tactIssues.map((issue, index) => {
                        const alertText = issue.message || (typeof issue === 'string' ? issue : '');
                        const issueKey = this.getIssueKey(issue, index);
                        const decision = this.state.alertDecisions[issueKey] || '';
                        const inputValue = this.state.alertInputs[issueKey] || '';

                        return (
                          <Stack direction='column' spacing={0} className={classes.alertItem} key={issueKey}>
                            <Typography className={classes.alertMessage} variant='subtitle2'>
                              {index + 1}. {alertText}
                            </Typography>
                            <InputRadioGroup
                              label=''
                              name={`alerts.${index}.decision`}
                              value={decision}
                              onChange={(e) => this.handleAlertDecisionChange(issueKey, e.target.value)}
                              options={[
                                {
                                  value: 'accept',
                                  label: 'Accept',
                                },
                                {
                                  value: 'ignore',
                                  label: 'Ignore',
                                },
                              ]}
                            />
                            <FormGroup
                              label='Enter value to update'
                              value={inputValue}
                              placeholder='Enter values, instructions, comments'
                              onChange={(e) => this.handleAlertInputChange(issueKey, e.target.value)}
                            />
                          </Stack>
                        );
                      })}

                    {hasAcceptedTactIssue && (
                      <Box display='flex' justifyContent='flex-end' mt={0.5} mb={0.5}>
                        <CustomButton
                          variant='contained'
                          color='primary'
                          size='small'
                          onClick={this.handleFixAllWithAi}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '13px',
                            py: 0.75,
                            px: 2.5,
                            borderRadius: '6px',
                          }}
                        >
                          Fix all with AI
                        </CustomButton>
                      </Box>
                    )}

                    {generalWarnings && generalWarnings.length > 0 && (
                      <>
                        <Divider sx={{
                          my: 1.5,
                          borderColor: '#e2e8f0' 
                        }} />

                        {/* FWB Validation Warnings Subsection */}
                        <div className={classes.warningsSubSection}>
                          <Stack direction='row' alignItems='center' justifyContent='space-between' mb={1.5}>
                            <Stack direction='row' alignItems='center' spacing={1}>
                              <InfoOutlined sx={{
                                color: 'var(--primaryblue)',
                                fontSize: 20 
                              }} />
                              <Typography variant='subtitle2' fontWeight={800} color='text.primary'>
                                FWB Validation Warnings
                              </Typography>
                            </Stack>
                            <span className={classes.warningCountBadge}>
                              {generalWarnings.length} {generalWarnings.length === 1 ? 'Warning' : 'Warnings'}
                            </span>
                          </Stack>

                          <Stack direction='column' spacing={1}>
                            {generalWarnings.map((warn, wIdx) => (
                              <div key={`${wIdx}-${warn.code || warn.line || wIdx}`} className={classes.simpleWarningBanner}>
                                <InfoOutlined sx={{
                                  fontSize: 18,
                                  color: 'var(--primaryblue)',
                                  flexShrink: 0 
                                }} />
                                <Typography variant='body2' className={classes.simpleWarningText}>
                                  {warn.line ? <strong>Line {warn.line}: </strong> : ''}{warn.message}
                                </Typography>
                              </div>
                            ))}
                          </Stack>
                        </div>
                      </>
                    )}
                  </Stack>
                </div>
              )}
            </Section>
          </Panel>
        </Group>

        {/* Send to Destination button at the bottom right outside the alerts container */}
        <Box display='flex' justifyContent='flex-end' mt={2} mb={0.5}>
          <CustomButton
            variant='contained'
            color='primary'
            onClick={() => toast.success('FWB sent!')}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 700,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
            }}
          >
          Send to Destination
          </CustomButton>
        </Box>
      </>
    );

    return (
      <>
        <StyledCard className={`${styles.h100} ${classes.fwbDataCard}`}>
          <Grid container spacing={2} alignItems='center' mb={2} position='relative' flexWrap='wrap' className={classes.fwbDataHeader}>
            <Grid item xs>
              <FlexDiv className={styles.mb0}>
                <StyledCardTitle>{this.props.activeView === 'AWB' ? 'AWB Data Elements | ' : 'FWB Data | '}{data?.awbNumber}</StyledCardTitle>
              </FlexDiv>
            </Grid>
            <Grid item xs='auto'>
              {this.props.activeView === 'AWB' && (
                <CustomButton
                  variant='contained'
                  color='primary'
                  size='small'
                  onClick={this.handleUpdateFwbFromForm}
                  disabled={this.state.isUpdatingFwb}
                >
                  {this.state.isUpdatingFwb ? (
                    <>
                      <CircularProgress size={16} color='inherit' sx={{ mr: 1 }} />
                        Updating FWB...
                    </>
                  ) : (
                    'Update the FWB'
                  )}
                </CustomButton>
              )}
            </Grid>
            <Grid item xs='auto'>
              <RadioGroup
                aria-labelledby='folder-view-radio-buttons-group'
                name='folder-view-radio-buttons-group'
                value={this.props.activeView}
                row
                onChange={this.props.onViewChange}
                className={`${styles.radioToggleGroup} ${styles.mlauto}`}
              >
                <CustomRadio
                  value={'AWB'}
                  control={<Radio className={styles.radioToggleBtn} />}
                  label={'AWB View'}
                  className={`${styles.radioToggle} ${this.props.activeView === 'AWB' ? styles.radioToggleBtnChecked : ''}`}
                  classes={{ label: styles.radioToggleLabel }}
                />
                <CustomRadio
                  value={'FWB'}
                  control={<Radio className={styles.radioToggleBtn} />}
                  label={'FWB View'}
                  className={`${styles.radioToggle} ${this.props.activeView === 'FWB' ? styles.radioToggleBtnChecked : ''}`}
                  classes={{ label: styles.radioToggleLabel }}
                />
              </RadioGroup>
            </Grid>
          </Grid>

          <div
            className={`${classes.awbFormWrapperScroll} ${classes.viewTransition}`}
            key={this.props.activeView === 'AWB' ? 'awb-view' : 'fwb-view'}
          >
            {this.props.activeView === 'AWB' && (
              awbElements
            )}
            {this.props.activeView === 'FWB' && fwbData}
          </div>

        </StyledCard>

        <FwbVersionPopover
          open={Boolean(this.state.versionPopoverAnchorEl)}
          anchorEl={this.state.versionPopoverAnchorEl}
          onClose={() => this.setState({ versionPopoverAnchorEl: null })}
          versions={fwbVersions}
          selectedVersion={selectedVersionNum}
          onSelectVersion={(ver) => {
            this.setState({
              versionPopoverAnchorEl: null,
              previewVersionModalOpen: true,
              previewVersionData: ver,
            });
          }}
        />

        <FwbVersionPreviewModal
          open={this.state.previewVersionModalOpen}
          version={this.state.previewVersionData}
          selectedVersion={selectedVersionNum}
          onClose={() => this.setState({
            previewVersionModalOpen: false,
            previewVersionData: null 
          })}
          onSetMainVersion={(verNum) => {
            this.setState({
              previewVersionModalOpen: false,
              previewVersionData: null 
            });
            if (this.props.onSelectFwbVersion) {
              this.props.onSelectFwbVersion(verNum);
            }
          }}
        />

        <FwbHistoryPopover
          open={Boolean(this.state.historyPopoverAnchorEl)}
          anchorEl={this.state.historyPopoverAnchorEl}
          onClose={() => this.setState({ historyPopoverAnchorEl: null })}
          docId={this.state.data?.docId || this.props.data?.docId || this.props.docId}
        />

        <FwbDiffModal
          open={this.state.formDiffModalOpen}
          onClose={this.handleRejectFormDiff}
          oldText={this.state.formDiffOldText}
          newText={this.state.formDiffNewText}
          onAccept={(e) => this.handleAcceptFormDiff(e?.currentTarget)}
          onReject={this.handleRejectFormDiff}
        />

        <FwbSaveModal
          open={this.state.saveModalOpen}
          anchorEl={this.state.saveModalAnchorEl}
          onClose={() => this.setState({
            saveModalOpen: false,
            saveModalAnchorEl: null,
            saveContext: null,
          })}
          onSave={this.handleSaveModalConfirm}
          currentVersion={selectedVersionNum}
          currentVersionName={currentVersionObj.name}
          nextVersion={nextVerNum}
          isLoading={this.state.isSavingFwb}
        />

        {this.state.modal === 'accountingInformation' && <AccountingInfoModal 
          modal={this.state.modal}
          onClose={this.handleClose}
        />}
      </>
    );
  }
}

export default FwbData;
